// Imports
import React from 'react';
import theme from '../../../theme';
import styled from 'styled-components/macro';
import { setSaturation, setLightness } from 'polished';


// Initialize
const spacing = 2;


// Styled components
const Container = styled.header`
	margin: 0 auto;
	padding: 7rem 0 1.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	line-height: 1;
	width: calc(100% - ${spacing}rem);
	max-width: calc(100% - ${spacing}rem);
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		padding-top: 3rem;
	}
`;

const CustomizedSide = styled.div`
	margin: auto 1.5rem auto 0;
	display: block;
	vertical-align: middle;
	max-width: 250px;
	font-size: 1.5em;
	font-weight: 300;
	text-align: center;
	color: ${setSaturation(0.25, setLightness(0.24, theme.colors.primary))};
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		margin-right: 1rem;
		max-width: 45vw;
	}
`;

const CustomName = styled.span`
	white-space: nowrap;
	text-overflow: ellipsis;
	max-width: 100%;
	line-height: 1.15em; /* Prevents clipping of some letters */
	display: inline-block;
	overflow: hidden;
`;

const PoweredBy = styled.div`
	display: flex;
	font-size: 0.8rem;
	align-items: center;
	justify-content: center;
	gap: 0.3em;
	
	> span {
		text-align: right;
	}
`;

const SmallLogo = styled.div`
	> a {
		display: block;
		line-height: 0;
		text-decoration: none;
		
		> svg,
		img {
			width: auto;
			height: 1.2em;
		}
	}
`;

const BigLogo = styled.div`
	margin: auto 1.5rem auto 0;
	display: block;
	vertical-align: middle;
	width: calc(48% - ${spacing / 2}rem);
	max-width: 200px;
	
	> a {
		display: block;
		line-height: 0;
		text-decoration: none;
		
		> svg,
		img {
			width: 100%;
		}
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		margin-right: 1rem;
	}
`;

const H1 = styled.h1`
	margin: auto 0;
	padding: 0 0 0 1.5rem;
	border-left: 1px solid ${setSaturation(0.1, setLightness(0.8, theme.colors.primary))};
	font-size: 2em;
	font-weight: 300;
	color: ${setSaturation(0.25, setLightness(0.24, theme.colors.primary))};
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		font-size: 1.5em;
		padding-left: 1rem;
	}
`;


// Define the accepted props
interface Props {
	logoElement: React.ReactElement;
	title: string;
	storybookCustomName?: string;
}


// Get custom name
// Function component
const Header: React.FC<Props> = ({ logoElement, title, storybookCustomName }) => {
	// Determine custom name
	const customName = new URLSearchParams(document.location.search).get('custom-name') || storybookCustomName;
	
	
	// Return JSX
	return (
		<Container>
			{customName ? (
				<CustomizedSide>
					<CustomName>{customName}</CustomName>
					<PoweredBy>
						<span>Powered by</span>
						<SmallLogo>
							<a
								href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/`}
								target='_blank'
								rel='noopener noreferrer'
								aria-label='Go to company website'
							>
								{logoElement}
							</a>
						</SmallLogo>
					</PoweredBy>
				</CustomizedSide>
			) : (
				<BigLogo>
					<a
						href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/`}
						target='_blank'
						rel='noopener noreferrer'
						aria-label='Go to company website'
					>
						{logoElement}
					</a>
				</BigLogo>
			)}
			
			<H1>{title}</H1>
		</Container>
	);
};

export default Header;
